import React from 'react';
import { useFeature } from '../../hooks/useFeatureFlags';

interface FeatureGateProps {
  feature: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  loadingFallback?: React.ReactNode;
}

/**
 * FeatureGate component for conditional rendering based on feature flags
 * 
 * Usage:
 * <FeatureGate feature="portfolio_snapshot" fallback={<UpgradePrompt />}>
 *   <PortfolioSnapshot />
 * </FeatureGate>
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({ 
  feature, 
  children, 
  fallback = null,
  loadingFallback = null 
}) => {
  const isEnabled = useFeature(feature as any);

  // If feature is enabled, render children
  if (isEnabled) {
    return <>{children}</>;
  }

  // Otherwise render fallback
  return <>{fallback}</>;
};

interface MultiFeatureGateProps {
  features: string[];
  requireAll?: boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * MultiFeatureGate for checking multiple features
 * 
 * Usage:
 * <MultiFeatureGate features={['messaging', 'video_messages']} requireAll>
 *   <VideoMessaging />
 * </MultiFeatureGate>
 */
export const MultiFeatureGate: React.FC<MultiFeatureGateProps> = ({ 
  features, 
  requireAll = false,
  children, 
  fallback = null 
}) => {
  const enabledFeatures = features.map(feature => useFeature(feature as any));

  const isEnabled = requireAll 
    ? enabledFeatures.every(enabled => enabled)
    : enabledFeatures.some(enabled => enabled);

  return isEnabled ? <>{children}</> : <>{fallback}</>;
};

/**
 * Higher-order component for feature gating
 * 
 * Usage:
 * const ProtectedComponent = withFeature('portfolio_snapshot')(MyComponent);
 */
export function withFeature<P extends object>(
  feature: string,
  FallbackComponent?: React.ComponentType<P>
) {
  return function WithFeatureComponent(Component: React.ComponentType<P>) {
    return function FeatureGatedComponent(props: P) {
      const isEnabled = useFeature(feature as any);

      if (isEnabled) {
        return <Component {...props} />;
      }

      if (FallbackComponent) {
        return <FallbackComponent {...props} />;
      }

      return null;
    };
  };
}

/**
 * Feature unavailable message component
 */
export const FeatureUnavailable: React.FC<{ featureName: string }> = ({ featureName }) => {
  return (
    <div className="text-center py-12 px-4">
      <div className="mx-auto max-w-sm">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          {featureName} is not available
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          This feature is not enabled for your account. Contact your administrator to request access.
        </p>
      </div>
    </div>
  );
};

/**
 * Feature coming soon component
 */
export const FeatureComingSoon: React.FC<{ featureName: string; expectedDate?: string }> = ({ 
  featureName, 
  expectedDate 
}) => {
  return (
    <div className="text-center py-12 px-4">
      <div className="mx-auto max-w-sm">
        <svg
          className="mx-auto h-12 w-12 text-amber-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          {featureName} coming soon
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          We're working hard to bring you this feature.
          {expectedDate && ` Expected ${expectedDate}.`}
        </p>
      </div>
    </div>
  );
};